import { Box, Flex, Icon, IconButton, Stack, Text } from '@chakra-ui/react';
import { sizeVars } from '@duke-hq/ui/constants/layout';
import { pageContentPadding } from '@duke-hq/ui/theme/tokens';
import { XIcon } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { EditorMode } from '@duke-hq/shared-constants/assessments';
import { unitAssessmentState } from '../UnitAssessmentState';
import { useSnapshot } from 'valtio';
import { OpenQuestionEditor } from './edit/OpenQuestionEditor';
import { QuestionGroupEditor } from './edit/QuestionGroupEditor';
import { MultiChoiceQuestionEditor } from './edit/MultiChoiceQuestionEditor';
import { ContentEditor } from './edit/ContentEditor';
import { FormEditor } from './edit/FormEditor';
import { FormSectionEditor } from './edit/FormSectionEditor';
import { FormFieldEditor } from './edit/FormFieldEditor';
import { FileUploadPromptEditor } from './edit/FileUploadPromptEditor';
import { SlideKind } from '@duke-hq/svc-api-db';

export function AssessmentSlideEditor({ refetch }: { refetch: () => void }) {
  const unitAssessmentSnap = useSnapshot(unitAssessmentState);

  const slideTypeTexts = {
    [SlideKind.OPEN_QUESTION]: 'Open Question',
    [SlideKind.CONTENT]: 'Content',
    [SlideKind.QUESTION_GROUP]: 'Question Group',
    [SlideKind.FILE_UPLOAD_PROMPT]: 'File Upload Prompt',
    [SlideKind.FORM]: 'Interactive Form',
    MULTI_CHOICE_QUESTION: 'Multi Choice Question',
    FORM_SECTION: 'Form Section',
    FORM_FIELD: 'Field',
  };

  if (unitAssessmentSnap.isEditorOpen === false) {
    return null;
  }

  return (
    <Box
      w="60%"
      overflowY="auto"
      position="sticky"
      top={sizeVars.navbar}
      bg="bg.surface"
      px={pageContentPadding.x}
      py={pageContentPadding.y}
    >
      <Stack spacing={0} h="full">
        <Flex justifyContent="space-between">
          <Text textStyle="xl" fontWeight="medium">
            {unitAssessmentSnap.editorMode === EditorMode.CREATE ? (
              <Trans>Add</Trans>
            ) : (
              <Trans>Edit</Trans>
            )}
            &nbsp;
            {slideTypeTexts[unitAssessmentSnap.editorType] || null}
          </Text>
          <IconButton
            aria-label="Close"
            variant="ghost"
            size="sm"
            icon={<Icon as={XIcon} color="fg.default" boxSize="18px" />}
            onClick={unitAssessmentState.closeEditor}
          />
        </Flex>
        <Box mx="auto" w="full" h="full">
          {unitAssessmentSnap.editorType === SlideKind.OPEN_QUESTION && (
            <OpenQuestionEditor refetch={refetch} />
          )}
          {unitAssessmentSnap.editorType === SlideKind.QUESTION_GROUP && (
            <QuestionGroupEditor refetch={refetch} />
          )}
          {unitAssessmentSnap.editorType === 'MULTI_CHOICE_QUESTION' && (
            <MultiChoiceQuestionEditor refetch={refetch} />
          )}
          {unitAssessmentSnap.editorType === SlideKind.CONTENT && (
            <ContentEditor refetch={refetch} />
          )}
          {unitAssessmentSnap.editorType === SlideKind.FILE_UPLOAD_PROMPT && (
            <FileUploadPromptEditor refetch={refetch} />
          )}
          {unitAssessmentSnap.editorType === SlideKind.FORM && (
            <FormEditor
              key={unitAssessmentSnap.currentSlide?.id ?? 'new-form-slide'}
              refetch={refetch}
            />
          )}
          {unitAssessmentSnap.editorType === 'FORM_SECTION' && (
            <FormSectionEditor
              key={unitAssessmentSnap.currentFormSectionId ?? 'new-form-section'}
              refetch={refetch}
            />
          )}
          {unitAssessmentSnap.editorType === 'FORM_FIELD' && (
            <FormFieldEditor
              key={unitAssessmentSnap.currentFormFieldId ?? 'new-form-field'}
              refetch={refetch}
            />
          )}
        </Box>
      </Stack>
    </Box>
  );
}
